import React, { useState, useCallback, useRef, useEffect } from 'react';
import confetti from 'canvas-confetti';
import { usePassValidation } from '../../hooks/usePassValidation';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Loader2, X } from 'lucide-react';
import CountryCodeSelect from '../common/CountryCodeSelect';

/**
 * PassAccordion Component (Simplified - No Pass Codes)
 * Used only for claiming new passes
 */
const PassAccordion = ({
  passType, // 'gold' or 'silver'
  companyName,
  attendeeDetails, // If pass is already claimed (for readOnly mode)
  allPasses, // All existing passes for validation
  readOnly, // If true, form is disabled
  onClaim,
  onCancel
}) => {
  const buttonRef = useRef(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    designation: '',
    company: companyName || '',
    phone: {
      countryCode: '91', // Store digits only, no '+'
      number: ''
    },
    email: ''
  });

  const [isClaiming, setIsClaiming] = useState(false);

  const { errors, validateAll, clearFieldError } = usePassValidation(
    formData,
    allPasses
  );

  // Load existing data if pass is claimed (readOnly mode)
  useEffect(() => {
    if (readOnly && attendeeDetails) {
      setFormData({
        firstName: attendeeDetails.firstName || '',
        lastName: attendeeDetails.lastName || '',
        designation: attendeeDetails.designation || '',
        company: attendeeDetails.company || companyName || '',
        phone: {
          countryCode: attendeeDetails.phone?.countryCode?.replace('+', '') || '91',
          number: attendeeDetails.phone?.number || ''
        },
        email: attendeeDetails.email || ''
      });
    } else {
      // For new passes, ensure company name is set
      setFormData(prev => ({ ...prev, company: companyName || '' }));
    }
  }, [readOnly, attendeeDetails, companyName]);

  // Handle field change
  const handleFieldChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    clearFieldError(field);
  }, [clearFieldError]);

  // Handle country code change
  const handleCountryCodeChange = useCallback((code) => {
    // Strip '+' and store only digits
    const cleanCode = code.replace('+', '');
    setFormData(prev => ({
      ...prev,
      phone: { ...prev.phone, countryCode: cleanCode }
    }));
  }, []);

  // Handle phone number change
  const handlePhoneNumberChange = useCallback((value) => {
    // Strip all non-digits
    const cleanNumber = value.replace(/\D/g, '');
    setFormData(prev => ({
      ...prev,
      phone: { ...prev.phone, number: cleanNumber }
    }));
    // Clear error when user types a valid phone number
    if (cleanNumber.length === 10) {
      clearFieldError('phone');
    }
  }, [clearFieldError]);

  // Handle claim
  const handleClaim = async () => {
    // Validate all fields
    const isValid = validateAll();

    if (!isValid) {
      return;
    }

    setIsClaiming(true);

    try {
      await onClaim(passType, formData);

      // Trigger confetti from button position
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const x = (rect.left + rect.width / 2) / window.innerWidth;
        const y = (rect.top + rect.height / 2) / window.innerHeight;

        confetti({
          particleCount: 100,
          spread: 70,
          origin: { x, y },
          colors: passType === 'gold'
            ? ['#FFD700', '#FFA500', '#FF8C00']
            : ['#C0C0C0', '#A9A9A9', '#808080']
        });
      }
    } catch (error) {
      console.error('Failed to claim pass:', error);
    } finally {
      setIsClaiming(false);
    }
  };

  const isFormValid = () => {
    return (
      formData.firstName &&
      formData.lastName &&
      formData.designation &&
      formData.company &&
      formData.phone.number &&
      formData.email &&
      Object.keys(errors).length === 0
    );
  };

  return (
    <Card className={`border-2 ${passType === 'gold' ? 'border-yellow-400' : 'border-gray-400'}`}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">
            Claim {passType === 'gold' ? 'Gold' : 'Silver'} Pass
          </CardTitle>
          <Button
            variant="ghost"
            size="icon"
            onClick={onCancel}
            className="h-8 w-8 hover:text-gray-900"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* First Name */}
        <div>
          <Label htmlFor="firstName">
            First Name <span className="text-red-500">*</span>
          </Label>
          <Input
            id="firstName"
            value={formData.firstName}
            onChange={(e) => handleFieldChange('firstName', e.target.value)}
            className={errors.firstName ? 'border-red-500' : ''}
            placeholder="Enter first name"
          />
          {errors.firstName && (
            <p className="text-xs text-red-500 mt-1">{errors.firstName}</p>
          )}
        </div>

        {/* Last Name */}
        <div>
          <Label htmlFor="lastName">
            Last Name <span className="text-red-500">*</span>
          </Label>
          <Input
            id="lastName"
            value={formData.lastName}
            onChange={(e) => handleFieldChange('lastName', e.target.value)}
            className={errors.lastName ? 'border-red-500' : ''}
            placeholder="Enter last name"
          />
          {errors.lastName && (
            <p className="text-xs text-red-500 mt-1">{errors.lastName}</p>
          )}
        </div>

        {/* Designation */}
        <div>
          <Label htmlFor="designation">
            Designation <span className="text-red-500">*</span>
          </Label>
          <Input
            id="designation"
            value={formData.designation}
            onChange={(e) => handleFieldChange('designation', e.target.value)}
            className={errors.designation ? 'border-red-500' : ''}
            placeholder="e.g., CEO, CTO, Marketing Manager"
          />
          {errors.designation && (
            <p className="text-xs text-red-500 mt-1">{errors.designation}</p>
          )}
        </div>

        {/* Company (Read-only) */}
        <div>
          <Label htmlFor="company">
            Company <span className="text-red-500">*</span>
          </Label>
          <Input
            id="company"
            value={formData.company}
            disabled
            className="bg-gray-100 cursor-not-allowed"
          />
        </div>

        {/* Phone Number */}
        <div>
          <Label htmlFor="phone">
            Phone Number <span className="text-red-500">*</span>
          </Label>
          <div className="flex gap-2">
            <div className="w-[140px]">
              <CountryCodeSelect
                value={formData.phone.countryCode}
                onChange={handleCountryCodeChange}
                disabled={readOnly}
              />
            </div>
            <Input
              id="phone"
              type="tel"
              placeholder="9876543210"
              value={formData.phone.number}
              onChange={(e) => handlePhoneNumberChange(e.target.value)}
              disabled={readOnly}
              className={errors.phone ? 'border-red-500' : ''}
              maxLength={10}
            />
          </div>
          {errors.phone && (
            <p className="text-xs text-red-500 mt-1">{errors.phone}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <Label htmlFor="email">
            Email <span className="text-red-500">*</span>
          </Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleFieldChange('email', e.target.value)}
            className={errors.email ? 'border-red-500' : ''}
            placeholder="email@example.com"
          />
          {errors.email && (
            <p className="text-xs text-red-500 mt-1">{errors.email}</p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 mt-6">
          <Button
            ref={buttonRef}
            onClick={handleClaim}
            disabled={!isFormValid() || isClaiming}
            className="flex-1"
            variant={isFormValid() ? 'default' : 'secondary'}
          >
            {isClaiming ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Claiming Pass...
              </>
            ) : (
              'Claim Pass'
            )}
          </Button>
          <Button
            onClick={onCancel}
            variant="outline"
            disabled={isClaiming}
            className="hover:text-gray-900"
          >
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PassAccordion;
